import React from "react";
import axios from "axios";

import { BACKEND_CONFIG } from "../config/consts";
import { removeZMarkers } from "../utils/actions";

const ActivityLogContext = React.createContext();

export class ActivityLogProvider extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      clientNotifications: [],
      agencyNotifications: [],
      unreadAgencyNotificationsCount: 0,
      allAgencyNotificationsLoaded: false,
      ready: false,
    };

    this.timer = null;
  }

  componentDidMount() {
    if (this.props.auth) {
      this.reloadPage();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.auth && !this.props.auth) {
      clearTimeout(this.timer);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  reloadPage = () => {
    this.setState(
      {
        ready: false,
      },
      async () => {
        await this.loadUnreadAgencyNotificationsCount();
        this.clearLoadedAgencyNotifications();
        this.timer = setTimeout(() => this.reloadPage(), 5 * 60 * 1000);
      }
    );
  };

  loadAgencyClientNotifications = async (clientId) => {
    let loadedAgencyNotifications = this.getAgencyNotifications();
    let loadedAgencyClientNotifications = loadedAgencyNotifications.find(
      (clientNotifications) => clientNotifications.ClientId === clientId
    );

    if (loadedAgencyClientNotifications.AllNotificationsLoaded) {
      return;
    }

    let response = await this.requestClientNotifications("client", clientId);
    let newNotifications = response.data.Notifications;

    loadedAgencyClientNotifications.Notifications = newNotifications;
    loadedAgencyClientNotifications.MostRecentNotificationDate =
      newNotifications[0].EventDate;
    loadedAgencyClientNotifications.AllNotificationsLoaded = true;
    this.setState({
      agencyNotifications: loadedAgencyNotifications,
      unreadAgencyNotificationsCount: this.refreshUnreadNotificationsCount(
        loadedAgencyNotifications
      ),
    });
  };

  loadClientNotifications = async (clientId) => {
    let response = await this.requestClientNotifications(
      "fourpanel/client",
      clientId
    );
    let loadedClientNotifications = response.data.Notifications;
    let clientUnreadNotificationsCount =
      loadedClientNotifications.UnreadEventsCount;

    this.setState({
      clientNotifications: loadedClientNotifications,
      unreadAgencyNotificationsCount:
        clientUnreadNotificationsCount >
        this.state.unreadAgencyNotificationsCount
          ? clientUnreadNotificationsCount
          : this.state.unreadAgencyNotificationsCount,
    });
  };

  requestClientNotifications = (path, clientId) =>
    this.get(path, {
      clientId: clientId,
      from: 0,
      size: 10000,
    });

  clearLoadedAgencyNotifications = () => {
    this.setState({
      agencyNotifications: [],
      allAgencyNotificationsLoaded: false,
    });
  };

  loadMoreAgencyNotifications = async () => {
    const pageSize = 10;
    let notifications = this.getAgencyNotifications();
    let response = await this.get("agency", {
      agencyId: BACKEND_CONFIG.get("agencyid"),
      from: notifications.length,
      size: pageSize,
    });
    let newAgencyNotifications = response.data.map((clientNotifications) => {
      let notifications = clientNotifications.Notifications;
      let firstNotification = notifications[0];

      return Object.assign({}, clientNotifications, {
        AllNotificationsLoaded: notifications.length < pageSize,
        Notifications: notifications,
        ClientId: firstNotification.ClientId,
        MostRecentNotificationDate: firstNotification.EventDate,
      });
    });
    let loadedAgencyNotifications = notifications.concat(
      newAgencyNotifications
    );

    this.setState({
      agencyNotifications: loadedAgencyNotifications,
      allAgencyNotificationsLoaded: newAgencyNotifications.length < pageSize,
      unreadAgencyNotificationsCount: this.refreshUnreadNotificationsCount(
        loadedAgencyNotifications
      ),
    });
  };

  loadUnreadAgencyNotificationsCount = async () => {
    let response = await this.get("unreadcount", {
      agencyId: BACKEND_CONFIG.get("agencyid"),
    });
    let newUnreadAgencyNotificationsCount = response.data;

    this.setState({
      unreadAgencyNotificationsCount: newUnreadAgencyNotificationsCount,
      ready: true,
    });

    return newUnreadAgencyNotificationsCount;
  };

  getQuoteNotifications = (quoteId) =>
    this.state.clientNotifications.filter((n) => n.QuoteId === quoteId);

  getQuoteUnreadNotificationsCount = (quoteId) =>
    this.state.clientNotifications.filter(
      (n) => n.QuoteId === quoteId && !n.Read
    ).length;

  getAgencyNotifications = () => {
    return this.state.agencyNotifications.map((clientNotifications) => {
      return Object.assign({}, clientNotifications, {
        Notifications: clientNotifications.Notifications.map((n) =>
          Object.assign({}, n)
        ),
      });
    });
  };

  readNotification = (notificationId, markAsRead) => {
    return this.readSomeNotifications(
      (n) => n.Key === notificationId,
      markAsRead,
      `${markAsRead ? "markasread" : "markasunread"}/${notificationId}`
    );
  };

  readQuoteNotifications = (quoteId, markAsRead) => {
    return this.readSomeNotifications(
      (n) => n.QuoteId === quoteId,
      markAsRead,
      `${markAsRead ? "quote/markallasread" : "quote/markallasunread"}`,
      {
        quoteId: quoteId,
        mostRecentNotificationDate: removeZMarkers(
          this.state.clientNotifications[0].EventDate
        ),
      }
    );
  };

  readClientNotifications = (clientId, markAsRead) => {
    return this.readSomeNotifications(
      (n) => n.ClientId === clientId,
      markAsRead,
      `${markAsRead ? "client/markallasread" : "client/markallasunread"}`,
      {
        clientId: clientId,
        mostRecentNotificationDate: this.getAgencyNotificationsMostRecentDate(),
      }
    );
  };

  readAllNotifications = async () => {
    let agencyId = BACKEND_CONFIG.get("agencyid");
    let newState = await this.readNotifications(
      (n) => n.AgencyId === agencyId,
      true,
      "agency/markallasread",
      {
        agencyId: agencyId,
        mostRecentNotificationDate: this.getAgencyNotificationsMostRecentDate(),
      },
      0
    );

    newState.agencyNotifications.forEach(
      (clientNotifications) => (clientNotifications.UnreadEventsCount = 0)
    );
    this.setState(newState);
  };

  clearNotification = (notificationId) => {
    return this.clear(
      (n) => n.Key === notificationId,
      `clear/${notificationId}`
    );
  };

  clearClientNotifications = (clientId) => {
    return this.clear((n) => n.ClientId === clientId, "client/clearall", {
      clientId: clientId,
      mostRecentNotificationDate: this.getAgencyNotificationsMostRecentDate(),
    });
  };

  clearAgencyNotifications = () => {
    let agencyId = BACKEND_CONFIG.get("agencyid");

    return this.clear((n) => n.AgencyId === agencyId, "agency/clearall", {
      agencyId: agencyId,
      mostRecentNotificationDate: this.getAgencyNotificationsMostRecentDate(),
    });
  };

  async readSomeNotifications(condition, markAsRead, url, params) {
    let newState = await this.readNotifications(
      condition,
      markAsRead,
      url,
      params,
      this.updateUnreadNotificationsCount(condition, markAsRead)
    );

    this.setState(newState);
  }

  async readNotifications(condition, markAsRead, url, params, unreadCount) {
    await this.put(url, params);

    return {
      agencyNotifications: this.processAgencyNotifications(
        (n) => condition(n),
        (n) => (n.Read = markAsRead)
      ),
      clientNotifications: this.processClientNotifications(
        (n) => condition(n),
        (n) => (n.Read = markAsRead)
      ),
      unreadAgencyNotificationsCount: unreadCount,
    };
  }

  updateUnreadNotificationsCount(condition, markAsRead) {
    let newUnreadNotificationsCount = this.state.unreadAgencyNotificationsCount;
    let updateUnreadCount = (n) => {
      if (n.Read !== markAsRead) {
        newUnreadNotificationsCount += markAsRead ? -1 : 1;
      }
    };

    this.processAgencyNotifications(
      (n) => condition(n),
      (n) => updateUnreadCount(n)
    );

    if (
      newUnreadNotificationsCount === this.state.unreadAgencyNotificationsCount
    ) {
      this.processClientNotifications(
        (n) => condition(n),
        (n) => updateUnreadCount(n)
      );
    }

    return newUnreadNotificationsCount;
  }

  async clear(condition, url, params) {
    await this.put(url, params);
    this.setState({
      agencyNotifications: this.processAgencyNotifications(
        (n) => condition(n),
        (n) => {
          n.Hidden = true;
        }
      ),
    });
  }

  processAgencyNotifications(condition, action) {
    return this.state.agencyNotifications.map((clientNotifications) => {
      return Object.assign({}, clientNotifications, {
        Notifications: this.processNotifications(
          clientNotifications.Notifications,
          condition,
          action
        ),
      });
    });
  }

  processClientNotifications(condition, action) {
    return this.processNotifications(
      this.state.clientNotifications,
      condition,
      action
    );
  }

  processNotifications(notifications, condition, action) {
    return notifications.map((n) => {
      let newNotification = Object.assign({}, n);

      if (condition(newNotification)) {
        action(newNotification);
      }

      return newNotification;
    });
  }

  getAgencyNotificationsMostRecentDate() {
    return removeZMarkers(
      this.state.agencyNotifications[0].MostRecentNotificationDate
    );
  }

  refreshUnreadNotificationsCount(loadedAgencyNotifications) {
    let count = 0;

    loadedAgencyNotifications.forEach((clientNotifications) => {
      count += clientNotifications.AllNotificationsLoaded
        ? clientNotifications.Notifications.filter((n) => !n.Read).length
        : clientNotifications.UnreadEventsCount;
    });

    return count > this.state.unreadAgencyNotificationsCount
      ? count
      : this.state.unreadAgencyNotificationsCount;
  }

  put = (url, params) =>
    axios.put(`${BACKEND_CONFIG.apiUrl}activityhub/${url}`, null, {
      params: params,
    });

  get = (url, params) =>
    axios.get(`${BACKEND_CONFIG.apiUrl}activityhub/${url}`, {
      params: params,
    });

  render() {
    return (
      <ActivityLogContext.Provider
        value={{
          loadUnreadAgencyNotificationsCount:
            this.loadUnreadAgencyNotificationsCount,
          loadClientNotifications: this.loadClientNotifications,
          loadMoreAgencyNotifications: this.loadMoreAgencyNotifications,
          clearLoadedAgencyNotifications: this.clearLoadedAgencyNotifications,
          loadAgencyClientNotifications: this.loadAgencyClientNotifications,
          getQuoteNotifications: this.getQuoteNotifications,
          getQuoteUnreadNotificationsCount:
            this.getQuoteUnreadNotificationsCount,
          getAgencyNotifications: this.getAgencyNotifications,
          readNotification: this.readNotification,
          readAllNotifications: this.readAllNotifications,
          clearAgencyNotifications: this.clearAgencyNotifications,
          readQuoteNotifications: this.readQuoteNotifications,
          readClientNotifications: this.readClientNotifications,
          clearNotification: this.clearNotification,
          clearClientNotifications: this.clearClientNotifications,
          allAgencyNotificationsLoaded: this.state.allAgencyNotificationsLoaded,
          ready: this.state.ready,
          unreadAgencyNotificationsCount:
            this.state.unreadAgencyNotificationsCount,
        }}
      >
        {this.props.children}
      </ActivityLogContext.Provider>
    );
  }
}

export const ActivityLogConsumer = ActivityLogContext.Consumer;
